import { FileText, MapPin, PenTool as Tool, DollarSign, Calendar, CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { HomeHeader } from '../components/HomeHeader';

const steps = [
  {
    icon: FileText,
    title: 'Initial Consultation',
    description: 'Share your energy bills and requirements with our experts.',
  },
  {
    icon: MapPin,
    title: 'Site Assessment',
    description: 'We evaluate your property for optimal solar panel placement.',
  },
  {
    icon: Tool,
    title: 'Custom Design',
    description: 'Our engineers create a tailored solar system design.',
  },
  {
    icon: DollarSign,
    title: 'Financial Analysis',
    description: 'Review savings projections and financing options.',
  },
  {
    icon: Calendar,
    title: 'Installation',
    description: 'Professional installation by certified technicians.',
  },
  {
    icon: CheckCircle2,
    title: 'Activation',
    description: 'System testing and grid connection completion.',
  }
];

export function HowItWorks() {
  return (
    <div className="min-h-screen bg-white">
      <HomeHeader />
      
      <main>
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-blue-600 to-blue-700 text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                How Solar Installation Works
              </h1>
              <p className="text-xl text-blue-100 max-w-3xl mx-auto">
                A simple, straightforward process to bring clean energy to your property.
              </p>
            </div>
          </div>
        </div>

        {/* Process Steps */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mb-6">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex items-start mb-4">
                      <span className="bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center text-lg font-bold">
                        {index + 1}
                      </span>
                      <h3 className="text-xl font-bold ml-3">{step.title}</h3>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Timeline Visualization */}
        <div className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Project Timeline</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                From consultation to activation, here's what to expect.
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-200"></div>
              <div className="space-y-12">
                {steps.map((step, index) => (
                  <div key={index} className={`flex items-center ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-8 text-right' : 'md:pl-8'}`}>
                      <div className="bg-white p-6 rounded-lg shadow-lg">
                        <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                        <p className="text-gray-600">{step.description}</p>
                      </div>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center justify-center">
                      <div className="w-8 h-8 bg-blue-600 rounded-full text-white flex items-center justify-center">
                        {index + 1}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Frequently Asked Questions</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                Common questions about the solar installation process.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-bold mb-3">How long does installation take?</h3>
                <p className="text-gray-600">
                  Most residential installations are completed within 1-3 days, while commercial projects may take longer depending on size and complexity.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-bold mb-3">What permits are required?</h3>
                <p className="text-gray-600">
                  We handle all necessary permits, including building permits, electrical permits, and utility interconnection agreements.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-bold mb-3">Will my roof need modifications?</h3>
                <p className="text-gray-600">
                  Our team will assess your roof's structural integrity and make recommendations if any modifications are needed.
                </p>
              </div>

              <div className="bg-white rounded-lg p-6 shadow-lg">
                <h3 className="text-lg font-bold mb-3">What about maintenance?</h3>
                <p className="text-gray-600">
                  Solar panels require minimal maintenance. We provide regular check-ups and cleaning services as needed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-blue-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Begin your solar journey today with a free consultation.
            </p>
            <button className="bg-white text-blue-600 py-3 px-8 rounded-lg hover:bg-blue-50 font-medium">
              Schedule Consultation
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}